import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

export const TUTORIAL_COMPLETED_KEY = '@lnb_tutorial_completed';

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  targetTab: 'Home' | 'Work' | 'Clubs' | 'Ledger' | 'Network';
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 1,
    title: 'Home',
    description: 'Your personalized dashboard where you can:\n\n• View your daily feed\n• See latest updates from your network\n• Quick access to all features',
    targetTab: 'Home',
  },
  {
    id: 2,
    title: 'Work',
    description: 'Your business hub where you can:\n\n• Post contract opportunities\n• List supplies you need\n• Showcase your products\n• Connect with potential clients',
    targetTab: 'Work',
  },
  {
    id: 3,
    title: 'Circles',
    description: 'Join business communities where you can:\n\n• Create or join professional circles\n• Network with like-minded people\n• Share industry insights\n• Collaborate on projects',
    targetTab: 'Clubs',
  },
  {
    id: 4,
    title: 'Ledger',
    description: 'Manage your finances with:\n\n• Track all transactions\n• View payment history\n• Monitor shared ledgers\n• Keep financial records organized',
    targetTab: 'Ledger',
  },
  {
    id: 5,
    title: 'Network',
    description: 'Grow your connections by:\n\n• Discovering new professionals\n• Managing your contacts\n• Building meaningful relationships\n• Expanding your business reach',
    targetTab: 'Network',
  },
];

interface TutorialContextType {
  showTutorial: boolean;
  currentStep: number;
  totalSteps: number;
  currentStepData: TutorialStep | null;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  startTutorial: () => void;
  resetTutorial: () => Promise<void>;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Check if tutorial was completed on mount
  useEffect(() => {
    checkTutorialStatus();
  }, []);

  const checkTutorialStatus = async () => {
    try {
      const completed = await AsyncStorage.getItem(TUTORIAL_COMPLETED_KEY);
      if (!completed) {
        // Tutorial not completed, will be triggered by AuthContext after first login
        console.log('📚 Tutorial not completed yet');
      }
    } catch (error) {
      console.error('Error checking tutorial status:', error);
    }
  };

  const startTutorial = () => {
    console.log('📚 Starting tutorial');
    setCurrentStep(0);
    setShowTutorial(true);
  };

  const nextStep = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTutorial = async () => {
    await completeTutorial();
  };

  const completeTutorial = async () => {
    try {
      await AsyncStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
      setShowTutorial(false);
      setCurrentStep(0);
      console.log('✅ Tutorial completed');
    } catch (error) {
      console.error('Error completing tutorial:', error);
    }
  };

  const resetTutorial = async () => {
    try {
      await AsyncStorage.removeItem(TUTORIAL_COMPLETED_KEY);
      setShowTutorial(false);
      setCurrentStep(0);
      console.log('🔄 Tutorial reset - will show on next app launch');
    } catch (error) {
      console.error('Error resetting tutorial:', error);
    }
  };

  const currentStepData = showTutorial && currentStep < TUTORIAL_STEPS.length 
    ? TUTORIAL_STEPS[currentStep] 
    : null;

  const value: TutorialContextType = {
    showTutorial,
    currentStep,
    totalSteps: TUTORIAL_STEPS.length,
    currentStepData,
    nextStep,
    previousStep,
    skipTutorial,
    startTutorial,
    resetTutorial,
  };

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};
